/*
 * certificate-maker/frames.js — ornamental borders as PARAMETRIC VECTOR.
 *
 * WHY NOT A LIBRARY OF PICTURES
 * ─────────────────────────────
 * Every competitor surveyed on 2026-08-23 (gramotadel, gramotium,
 * gramota.online, Canva) ships its frames as raster images — a JPEG or PNG of
 * an ornament, laid under the text. Three consequences follow, and all three
 * are what people actually complain about:
 *
 *   1. It pixelates. A 1600 px wide JPEG on an A4 sheet is 96 dpi at the paper.
 *      Print it and the gold filigree turns to mush. The tail of the keyword
 *      cluster is full of «в хорошем качестве» / «высокого качества» for
 *      exactly this reason (measured via Wordstat 2026-08-23).
 *   2. It is heavy. A single ornate background is 300 KB–2 MB, and a batch of
 *      30 certificates re-embeds it 30 times unless the generator is careful.
 *   3. It cannot change. Wrong page size, wrong colour, wrong proportions —
 *      the picture was drawn for one aspect ratio and stretches on any other.
 *
 * A parametric frame has none of those properties. It is built from the page
 * dimensions at draw time, so A4 landscape, A4 portrait and A5 all get an
 * ornament drawn FOR them rather than stretched onto them; it is a few
 * kilobytes of path data; and it is resolution-free by construction — the same
 * file prints identically on a 300 dpi inkjet and a 2400 dpi imagesetter.
 *
 * THE GUILLOCHÉ IS THE POINT
 * ──────────────────────────
 * Real diplomas, share certificates and banknotes use guilloché — interleaved
 * harmonic curves — precisely because it is hard to reproduce and looks
 * expensive. It is also pure trigonometry, which means we can generate it and
 * a raster template library never can. `guilloche`, `braid` and `wave` below
 * are the same engine at three amplitudes.
 *
 * OUTPUT CONTRACT
 * ───────────────
 * build() returns primitives in MILLIMETRES, y DOWN from the top-left of the
 * page — the same model layout.js positions text in and the same one pdf.js
 * and render.js consume. Nothing here knows about pixels, points or the DOM,
 * so this file runs unchanged in Node (which is where its tests run).
 *
 *   { prims: [ {type:'path'|'rect'|'circle', ...} ], inset: mm }
 *
 * `inset` is how much room the ornament needs measured from the paper edge;
 * layout keeps text out of it. A frame that lies about its inset produces the
 * one defect that survives every review: text sitting on top of the filigree.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertFrames = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var TAU = Math.PI * 2;

    /* ── tiny path builder ───────────────────────────────────────────────────
     * Only M / L / C / Z are emitted. pdf-lib's SVG path parser handles arcs
     * too, but its 'A' implementation has rounding quirks on tiny radii and
     * the preview would then differ from the paper — the one failure this whole
     * architecture exists to prevent. Curves are cubic everywhere. */
    function P() { this.d = ''; }
    P.prototype.m = function (x, y) { this.d += 'M' + n(x) + ' ' + n(y); return this; };
    P.prototype.l = function (x, y) { this.d += 'L' + n(x) + ' ' + n(y); return this; };
    P.prototype.c = function (x1, y1, x2, y2, x, y) {
        this.d += 'C' + n(x1) + ' ' + n(y1) + ' ' + n(x2) + ' ' + n(y2) + ' ' + n(x) + ' ' + n(y);
        return this;
    };
    P.prototype.z = function () { this.d += 'Z'; return this; };
    P.prototype.poly = function (pts, close) {
        for (var i = 0; i < pts.length; i++) (i ? this.l : this.m).call(this, pts[i][0], pts[i][1]);
        if (close) this.z();
        return this;
    };
    /* Smooth closed curve through sample points (Catmull-Rom → cubic Bézier).
     * Sampling a harmonic and joining with straight lines needs ~8× the points
     * for the same smoothness; converting to Béziers keeps the file small AND
     * keeps the curve genuinely smooth under a loupe. */
    P.prototype.smooth = function (pts, close) {
        var nP = pts.length;
        if (nP < 2) return this;
        this.m(pts[0][0], pts[0][1]);
        var last = close ? nP : nP - 1;
        for (var i = 0; i < last; i++) {
            var p0 = pts[(i - 1 + nP) % nP], p1 = pts[i], p2 = pts[(i + 1) % nP], p3 = pts[(i + 2) % nP];
            if (!close) {
                if (i === 0) p0 = p1;
                if (i + 2 > nP - 1) p3 = p2;
            }
            this.c(
                p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6,
                p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6,
                p2[0], p2[1]
            );
        }
        if (close) this.z();
        return this;
    };
    /* Two decimals of a millimetre is 10 µm. A 2400 dpi imagesetter — the
     * finest device this file will ever meet — resolves 10.6 µm, so this is
     * already below the physical grid, and it costs a third of the bytes that
     * three decimals did (measured on the guilloché: 132 KB → 88 KB). */
    function n(v) { return (Math.round(v * 100) / 100); }

    function path(d, o) {
        o = o || {};
        return {
            type: 'path', d: d,
            color: o.color || null,          // stroke
            w: o.w == null ? 0.3 : o.w,      // stroke width, mm
            fill: o.fill || null,
            opacity: o.opacity == null ? 1 : o.opacity,
            dash: o.dash || null
        };
    }

    /* ── rounded-rectangle perimeter, sampled by arc length ──────────────────
     * Returns points with outward normals. Everything that runs "around the
     * frame" — guilloché, rope, dots, meander — is expressed as a function of
     * the distance travelled along this loop, which is what makes the pattern
     * continue around a corner instead of restarting at it. */
    function perimeter(x, y, w, h, r, step) {
        var pts = [], s = 0;
        r = Math.max(0, Math.min(r, Math.min(w, h) / 2));
        var straight = [
            { x0: x + r, y0: y,         x1: x + w - r, y1: y,         nx: 0,  ny: -1 },
            { x0: x + w, y0: y + r,     x1: x + w,     y1: y + h - r, nx: 1,  ny: 0  },
            { x0: x + w - r, y0: y + h, x1: x + r,     y1: y + h,     nx: 0,  ny: 1  },
            { x0: x, y0: y + h - r,     x1: x,         y1: y + r,     nx: -1, ny: 0  }
        ];
        var arcs = [
            { cx: x + w - r, cy: y + r,     a0: -Math.PI / 2, a1: 0 },
            { cx: x + w - r, cy: y + h - r, a0: 0,            a1: Math.PI / 2 },
            { cx: x + r,     cy: y + h - r, a0: Math.PI / 2,  a1: Math.PI },
            { cx: x + r,     cy: y + r,     a0: Math.PI,      a1: Math.PI * 1.5 }
        ];
        for (var i = 0; i < 4; i++) {
            var sg = straight[i];
            var len = Math.hypot(sg.x1 - sg.x0, sg.y1 - sg.y0);
            var cnt = Math.max(1, Math.round(len / step));
            for (var k = 0; k < cnt; k++) {
                var t = k / cnt;
                pts.push({ x: sg.x0 + (sg.x1 - sg.x0) * t, y: sg.y0 + (sg.y1 - sg.y0) * t, nx: sg.nx, ny: sg.ny, s: s + len * t });
            }
            s += len;
            if (r > 0.01) {
                var ar = arcs[i], alen = r * Math.PI / 2;
                var acnt = Math.max(2, Math.round(alen / step));
                for (var j = 0; j < acnt; j++) {
                    var a = ar.a0 + (ar.a1 - ar.a0) * (j / acnt);
                    pts.push({ x: ar.cx + Math.cos(a) * r, y: ar.cy + Math.sin(a) * r, nx: Math.cos(a), ny: Math.sin(a), s: s + alen * (j / acnt) });
                }
                s += alen;
            }
        }
        return { pts: pts, len: s };
    }

    /* A harmonic offset from the perimeter. `cycles` is rounded to an integer
     * so the wave meets itself at the seam — a non-integer count leaves a step
     * on the top edge that no one notices on screen and everyone notices on
     * paper. */
    function harmonic(loop, amp, cycles, phase, harm2, amp2) {
        var k = Math.round(cycles) * TAU / loop.len;
        var k2 = harm2 ? Math.round(cycles * harm2) * TAU / loop.len : 0;
        return loop.pts.map(function (p) {
            var o = Math.sin(p.s * k + phase) * amp + (harm2 ? Math.sin(p.s * k2 + phase * 1.7) * (amp2 || amp * 0.4) : 0);
            return [p.x + p.nx * o, p.y + p.ny * o];
        });
    }

    /* ── ornament pieces reused by several frames ────────────────────────── */

    /* Four-petal rosette / diamond used as a corner stud. */
    function stud(cx, cy, r) {
        var p = new P();
        p.m(cx, cy - r)
            .c(cx + r * 0.35, cy - r * 0.35, cx + r * 0.35, cy - r * 0.35, cx + r, cy)
            .c(cx + r * 0.35, cy + r * 0.35, cx + r * 0.35, cy + r * 0.35, cx, cy + r)
            .c(cx - r * 0.35, cy + r * 0.35, cx - r * 0.35, cy + r * 0.35, cx - r, cy)
            .c(cx - r * 0.35, cy - r * 0.35, cx - r * 0.35, cy - r * 0.35, cx, cy - r)
            .z();
        return p.d;
    }

    /* One laurel leaf: an almond (vesica) from base B to tip T. Built from two
     * mirrored cubics rather than two straight-ish arcs — the first attempt
     * used perpendicular control points off the stem and produced slivers that
     * read as barbed wire, not foliage (seen on the 2026-08-23 contact sheet). */
    function leaf(p, bx, by, tx, ty, wid) {
        var dx = tx - bx, dy = ty - by, L = Math.hypot(dx, dy) || 1;
        var ux = dx / L, uy = dy / L, px = -uy * wid, py = ux * wid;
        p.m(bx, by)
            .c(bx + ux * L * 0.18 + px, by + uy * L * 0.18 + py,
               bx + ux * L * 0.68 + px, by + uy * L * 0.68 + py, tx, ty)
            .c(bx + ux * L * 0.68 - px, by + uy * L * 0.68 - py,
               bx + ux * L * 0.18 - px, by + uy * L * 0.18 - py, bx, by)
            .z();
    }

    /* A laurel branch growing out of a corner along a quadratic stem. Leaves
     * are set at ~35° to the stem and point FORWARD (away from the base), which
     * is what makes a branch read as a branch; perpendicular leaves read as
     * thorns. Sizes taper to the tip and the base carries three berries. */
    function laurelSpray(x, y, len, sx, sy) {
        var p = new P(), q = new P(), leaves = Math.max(11, Math.round(len / 3.1));
        // Stem: quadratic from (0,0) to (len, 0) bowing INWARD, in local axes.
        // Outward looked better on screen and put the leaf tips ~2 mm from the
        // paper edge — inside the non-printable margin of every consumer
        // printer, so on paper the branch came out with its tips shaved off.
        function stem(t) { return { x: len * t, y: len * 0.17 * (t - t * t) * 4 }; }
        function stemDir(t) {
            var a = stem(Math.max(0, t - 0.01)), b = stem(Math.min(1, t + 0.01));
            var dx = b.x - a.x, dy = b.y - a.y, m = Math.hypot(dx, dy) || 1;
            return { x: dx / m, y: dy / m };
        }
        function W(q) { return [x + sx * q.x, y + sy * q.y]; }   // local → page

        var spts = [];
        for (var i = 0; i <= 24; i++) spts.push(W(stem(i / 24)));
        q.smooth(spts, false);          // stem: stroked, never filled

        for (var k = 0; k < leaves; k++) {
            var t = 0.06 + 0.92 * (k / (leaves - 1));
            var b = stem(t), d = stemDir(t);
            var taper = 1 - 0.5 * t;
            var Ll = len * 0.19 * taper, wid = len * 0.042 * taper;
            var side = (k % 2) ? 1 : -1;
            var a = Math.atan2(d.y, d.x) + side * 0.62;          // ≈35° off the stem
            var tip = { x: b.x + Math.cos(a) * Ll, y: b.y + Math.sin(a) * Ll };
            var B = W(b), T = W(tip);
            leaf(p, B[0], B[1], T[0], T[1], wid);
        }
        return { leaves: p.d, stem: q.d };
    }

    /* Three berries at the base of a laurel branch. */
    function berries(x, y, r, sx, sy) {
        return [
            { cx: x + sx * r * 1.1, cy: y + sy * r * 1.2 },
            { cx: x + sx * r * 2.6, cy: y + sy * r * 0.4 },
            { cx: x + sx * r * 2.2, cy: y + sy * r * 2.3 }
        ];
    }

    /* Logarithmic spiral, the terminal of a calligraphic stroke. */
    function spiralPts(cx, cy, r0, turns, a0, dir, grow) {
        var pts = [], N = Math.max(24, Math.round(turns * 26));
        for (var i = 0; i <= N; i++) {
            var t = i / N * TAU * turns;
            var r = r0 * Math.pow(grow || 2.4, t / TAU);
            var a = a0 + dir * t;
            pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
        }
        return pts;
    }

    /* Art-deco corner: nested right-angle brackets stepping inward. */
    function decoCorner(x, y, size, sx, sy, steps) {
        var p = new P();
        for (var i = 0; i < steps; i++) {
            var o = i * (size / (steps * 1.9));
            var L = size - i * (size / (steps * 1.35));
            if (L <= 0) break;
            p.m(x + sx * o, y + sy * (o + L)).l(x + sx * o, y + sy * o).l(x + sx * (o + L), y + sy * o);
        }
        return p.d;
    }

    /* A corner flourish: one long sweep hugging the corner that terminates in a
     * spiral at each end, plus a smaller inner echo. The first attempt drew two
     * free-hand S-curves and read as two unrelated loops; a real flourish is
     * ONE stroke whose ends curl, so that is what this builds. */
    function flourish(x, y, size, sx, sy) {
        var p = new P(), S = size;
        // Main sweep: from far along the horizontal edge, round the corner, up
        // the vertical edge. Expressed in local axes then mirrored by sx/sy.
        function W(lx, ly) { return [x + sx * lx, y + sy * ly]; }
        var A = W(S * 1.55, 0), A1 = W(S * 0.62, 0.02 * S), A2 = W(0.02 * S, S * 0.62), B = W(0, S * 1.55);
        p.m(A[0], A[1]).c(A1[0], A1[1], A2[0], A2[1], B[0], B[1]);
        // Terminal spirals at both ends, curling INWARD (toward the text block).
        p.smooth(spiralPts(x + sx * S * 1.62, y + sy * S * 0.30, S * 0.055, 1.15, sy > 0 ? -1.4 : 1.4, sx * sy > 0 ? 1 : -1, 2.5), false);
        p.smooth(spiralPts(x + sx * S * 0.30, y + sy * S * 1.62, S * 0.055, 1.15, sx > 0 ? -0.2 : 3.3, sx * sy > 0 ? -1 : 1, 2.5), false);
        // Inner echo — thinner, shorter, offset inward.
        var o = S * 0.30;
        var C = W(S * 1.16, o), C1 = W(S * 0.60, o + 0.02 * S), C2 = W(o + 0.02 * S, S * 0.60), D = W(o, S * 1.16);
        p.m(C[0], C[1]).c(C1[0], C1[1], C2[0], C2[1], D[0], D[1]);
        return p.d;
    }

    /* Five-pointed star, point up. */
    function star(cx, cy, r) {
        var pts = [];
        for (var i = 0; i < 10; i++) {
            var a = -Math.PI / 2 + i * Math.PI / 5, rr = (i % 2) ? r * 0.42 : r;
            pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
        }
        return new P().poly(pts, true).d;
    }

    /* Greek key (meander) run along one straight edge, `sx`/`sy` giving the
     * direction of travel and `px`/`py` the inward normal. */
    function meanderRun(x0, y0, len, dx, dy, px, py, unit, depth) {
        var p = new P(), count = Math.max(1, Math.floor(len / unit));
        var pad = (len - count * unit) / 2;
        for (var i = 0; i < count; i++) {
            var b = pad + i * unit;
            // Unit described as (along, inward) pairs of a single pen stroke.
            var u = [
                [0, 0], [0, depth], [unit * 0.72, depth], [unit * 0.72, depth * 0.28],
                [unit * 0.26, depth * 0.28], [unit * 0.26, depth * 0.72], [unit * 0.98, depth * 0.72], [unit * 0.98, 0]
            ];
            for (var k = 0; k < u.length; k++) {
                var a = b + u[k][0], d = u[k][1];
                var X = x0 + dx * a + px * d, Y = y0 + dy * a + py * d;
                (k ? p.l : p.m).call(p, X, Y);
            }
        }
        return p.d;
    }

    /* ── background guilloché rosette ───────────────────────────────────────
     * A hypotrochoid — the spirograph curve — not a polar flower. The first
     * attempt used r(θ)=R+a·cos(nθ), which is a daisy: pretty, but nothing like
     * the interference lattice on a share certificate. A hypotrochoid with a
     * non-trivial R:r ratio crosses itself, and it is precisely those crossings
     * that make a guilloché read as engraved rather than drawn.
     *
     *   x = (R−r)·cos t + d·cos((R−r)/r · t)
     *   y = (R−r)·sin t − d·sin((R−r)/r · t)
     *
     * The curve closes after r/gcd(R,r) turns, so the caller passes integers
     * and gets a closed figure — an unclosed one leaves a visible tail. */
    function gcd(a, b) { return b ? gcd(b, a % b) : a; }
    function hypotrochoid(cx, cy, scale, R, r, d, rot) {
        var turns = r / gcd(R, r);
        var pts = [], N = Math.max(240, Math.round(turns * 130));
        var k = (R - r) / r, sc = scale / ((R - r) + d);
        for (var i = 0; i < N; i++) {
            var t = i / N * TAU * turns;
            var x = ((R - r) * Math.cos(t) + d * Math.cos(k * t)) * sc;
            var y = ((R - r) * Math.sin(t) - d * Math.sin(k * t)) * sc;
            var ca = Math.cos(rot || 0), sa = Math.sin(rot || 0);
            pts.push([cx + x * ca - y * sa, cy + x * sa + y * ca]);
        }
        return new P().smooth(pts, true).d;
    }

    /* Kept for the medallion: a simple polar flower is right there, where the
     * figure is 10 mm across and a lattice would just fill in solid. */
    function rosette(cx, cy, R, petals, inner, turns) {
        var pts = [], N = Math.max(180, petals * 22);
        for (var i = 0; i <= N; i++) {
            var t = i / N * TAU * turns;
            var r = R * (1 - inner) + R * inner * Math.cos(petals * t);
            pts.push([cx + Math.cos(t) * r, cy + Math.sin(t) * r]);
        }
        return new P().smooth(pts, false).d;
    }

    /* ── the frames ──────────────────────────────────────────────────────── */

    var FRAMES = {

        none: { inset: 12, build: function () { return []; } },

        /* Two rules and four studs. The default for a reason: it is what a
         * printed Russian грамота blank actually looks like, and it never
         * fights the text. */
        classic: {
            inset: 17,
            build: function (o) {
                var m = 9, g = 3.2, out = [];
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 1.1 }));
                out.push(path(new P().poly(rect(m + g, m + g, o.w - 2 * (m + g), o.h - 2 * (m + g)), true).d, { color: o.ink, w: 0.3 }));
                corners(o, m + g, function (x, y) { out.push(path(stud(x, y, 1.9), { fill: o.accent, w: 0 })); });
                return out;
            }
        },

        /* Interleaved harmonics — the banknote look. Three phases of the same
         * wave read as a braid; the two hairlines contain it. */
        guilloche: {
            inset: 21,
            build: function (o) {
                var m = 8.5, band = 5.2, out = [];
                var loop = perimeter(m + band, m + band, o.w - 2 * (m + band), o.h - 2 * (m + band), 6, 1.6);
                var cycles = loop.len / 13;
                for (var i = 0; i < 3; i++) {
                    var pts = harmonic(loop, band * 0.86, cycles, i * TAU / 3, 2, band * 0.22);
                    out.push(path(new P().smooth(pts, true).d, { color: o.accent, w: 0.22, opacity: 0.95 }));
                }
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 0.9 }));
                out.push(path(new P().poly(rect(m + band * 2, m + band * 2, o.w - 2 * (m + band * 2), o.h - 2 * (m + band * 2)), true).d, { color: o.ink, w: 0.28 }));
                return out;
            }
        },

        /* Same engine, two phases and a bigger amplitude: a rope/twist. */
        braid: {
            inset: 20,
            build: function (o) {
                var m = 9, band = 4.4, out = [];
                var loop = perimeter(m + band, m + band, o.w - 2 * (m + band), o.h - 2 * (m + band), 5, 1.15);
                var cycles = loop.len / 9;
                out.push(path(new P().smooth(harmonic(loop, band * 0.9, cycles, 0), true).d, { color: o.accent, w: 0.5 }));
                out.push(path(new P().smooth(harmonic(loop, band * 0.9, cycles, Math.PI), true).d, { color: o.accent, w: 0.5 }));
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 0.8 }));
                out.push(path(new P().poly(rect(m + band * 2, m + band * 2, o.w - 2 * (m + band * 2), o.h - 2 * (m + band * 2)), true).d, { color: o.ink, w: 0.25 }));
                return out;
            }
        },

        /* One low, long wave inside a single rule — the quiet option for a
         * corporate certificate that must not look like a school prize. */
        wave: {
            inset: 16,
            build: function (o) {
                var m = 10, out = [];
                var loop = perimeter(m + 2.6, m + 2.6, o.w - 2 * (m + 2.6), o.h - 2 * (m + 2.6), 3, 2.6);
                out.push(path(new P().smooth(harmonic(loop, 2.1, loop.len / 22, 0), true).d, { color: o.accent, w: 0.35 }));
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 0.7 }));
                return out;
            }
        },

        /* Greek key. Runs the pattern along the four edges with mitred corner
         * squares, the way it is actually set in type. */
        meander: {
            inset: 20,
            build: function (o) {
                var m = 9.5, depth = 3.6, unit = 7.2, out = [];
                var x0 = m, y0 = m, W = o.w - 2 * m, H = o.h - 2 * m;
                var inset2 = depth + 1.4;
                out.push(path(new P().poly(rect(m, m, W, H), true).d, { color: o.ink, w: 0.8 }));
                out.push(path(new P().poly(rect(m + inset2, m + inset2, W - 2 * inset2, H - 2 * inset2), true).d, { color: o.ink, w: 0.5 }));
                var pad = inset2;
                out.push(path(meanderRun(x0 + pad, y0 + 0.7, W - 2 * pad, 1, 0, 0, 1, unit, depth), { color: o.accent, w: 0.42 }));
                out.push(path(meanderRun(x0 + W - 0.7, y0 + pad, H - 2 * pad, 0, 1, -1, 0, unit, depth), { color: o.accent, w: 0.42 }));
                out.push(path(meanderRun(x0 + W - pad, y0 + H - 0.7, W - 2 * pad, -1, 0, 0, -1, unit, depth), { color: o.accent, w: 0.42 }));
                out.push(path(meanderRun(x0 + 0.7, y0 + H - pad, H - 2 * pad, 0, -1, 1, 0, unit, depth), { color: o.accent, w: 0.42 }));
                return out;
            }
        },

        /* Art deco: stepped corner brackets plus a fan. */
        deco: {
            inset: 19,
            build: function (o) {
                var m = 10, out = [], size = Math.min(o.w, o.h) * 0.16;
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 1.4 }));
                out.push(path(new P().poly(rect(m + 2.4, m + 2.4, o.w - 2 * (m + 2.4), o.h - 2 * (m + 2.4)), true).d, { color: o.ink, w: 0.4 }));
                var q = [[m + 4.6, m + 4.6, 1, 1], [o.w - m - 4.6, m + 4.6, -1, 1], [o.w - m - 4.6, o.h - m - 4.6, -1, -1], [m + 4.6, o.h - m - 4.6, 1, -1]];
                q.forEach(function (c) {
                    out.push(path(decoCorner(c[0], c[1], size, c[2], c[3], 3), { color: o.accent, w: 0.75 }));
                });
                return out;
            }
        },

        /* Laurel sprays in the corners — the traditional Soviet/Russian
         * почётная грамота signature, still what people picture. */
        laurel: {
            inset: 21,
            build: function (o) {
                var m = 10, out = [], L = Math.min(o.w, o.h) * 0.23;
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 1.0 }));
                out.push(path(new P().poly(rect(m + 2.8, m + 2.8, o.w - 2 * (m + 2.8), o.h - 2 * (m + 2.8)), true).d, { color: o.ink, w: 0.28 }));
                var i = m + 7.5;
                [[i, i, 1, 1], [o.w - i, i, -1, 1], [i, o.h - i, 1, -1], [o.w - i, o.h - i, -1, -1]].forEach(function (c) {
                    var sp = laurelSpray(c[0], c[1], L, c[2], c[3]);
                    out.push(path(sp.stem, { color: o.accent, w: 0.55 }));
                    out.push(path(sp.leaves, { color: null, w: 0, fill: o.accent, opacity: 0.92 }));
                    berries(c[0], c[1], 1.15, c[2], c[3]).forEach(function (b) {
                        out.push({ type: 'circle', cx: b.cx, cy: b.cy, r: 0.85, fill: o.accent, color: null, w: 0 });
                    });
                });
                return out;
            }
        },

        /* Calligraphic flourishes — the "торжественная" look. */
        scroll: {
            inset: 20,
            build: function (o) {
                var m = 10, out = [], S = Math.min(o.w, o.h) * 0.17;
                out.push(path(new P().poly(rect(m, m, o.w - 2 * m, o.h - 2 * m), true).d, { color: o.ink, w: 0.5 }));
                var i = m + 3.4;
                [[i, i, 1, 1], [o.w - i, i, -1, 1], [i, o.h - i, 1, -1], [o.w - i, o.h - i, -1, -1]].forEach(function (c) {
                    out.push(path(flourish(c[0], c[1], S, c[2], c[3]), { color: o.accent, w: 0.5 }));
                });
                corners(o, m, function (x, y) { out.push(path(stud(x, y, 1.3), { fill: o.accent, w: 0 })); });
                return out;
            }
        },

        /* Corner brackets only. Minimal, modern, and the safest frame when the
         * sheet is a pre-printed blank being overprinted. */
        corners: {
            inset: 15,
            build: function (o) {
                var m = 9, out = [], L = Math.min(o.w, o.h) * 0.13;
                var q = [[m, m, 1, 1], [o.w - m, m, -1, 1], [o.w - m, o.h - m, -1, -1], [m, o.h - m, 1, -1]];
                q.forEach(function (c) {
                    out.push(path(new P().m(c[0], c[1] + c[3] * L).l(c[0], c[1]).l(c[0] + c[2] * L, c[1]).d, { color: o.ink, w: 1.3 }));
                    out.push(path(new P().m(c[0] + c[2] * 2.6, c[1] + c[3] * (L * 0.72)).l(c[0] + c[2] * 2.6, c[1] + c[3] * 2.6).l(c[0] + c[2] * (L * 0.72), c[1] + c[3] * 2.6).d, { color: o.accent, w: 0.4 }));
                });
                return out;
            }
        },

        /* Children's: a thick rounded band, a dashed inner rule and scattered
         * stars. Deliberately loud — «шаблон грамоты для детей» is 498/mo of
         * its own and a filigree border is wrong for it. */
        kids: {
            inset: 20,
            build: function (o) {
                var m = 8, out = [];
                // Scalloped outer edge — a low harmonic on the perimeter. A plain
                // rounded band read as "corporate with a thick stroke"; scallops
                // are what a child's certificate actually looks like.
                var sl = perimeter(m + 2.2, m + 2.2, o.w - 2 * (m + 2.2), o.h - 2 * (m + 2.2), 10, 2.2);
                out.push(path(new P().smooth(harmonic(sl, 2.2, sl.len / 11, 0), true).d, { color: o.accent, w: 2.2 }));
                out.push(path(new P().smooth(perimeter(m + 7.5, m + 7.5, o.w - 2 * (m + 7.5), o.h - 2 * (m + 7.5), 7, 3).pts.map(function (p) { return [p.x, p.y]; }), true).d,
                    { color: o.ink, w: 0.45, dash: [2.4, 2.2] }));
                // Confetti: alternating dots on the band, evenly spaced by arc
                // length so they turn the corner instead of piling up in it.
                var dl = perimeter(m + 5, m + 5, o.w - 2 * (m + 5), o.h - 2 * (m + 5), 6, 1);
                var dots = Math.max(24, Math.round(dl.len / 13));
                for (var i = 0; i < dots; i++) {
                    var q = dl.pts[Math.floor(dl.pts.length * i / dots)];
                    out.push({ type: 'circle', cx: q.x, cy: q.y, r: (i % 2) ? 0.75 : 1.15, fill: (i % 2) ? o.ink : o.accent, color: null, w: 0 });
                }
                corners(o, m + 5, function (x, y) { out.push(path(star(x, y, 4.2), { fill: o.accent, w: 0 })); });
                out.push(path(star(o.w / 2, m + 5, 3.4), { fill: o.ink, w: 0 }));
                out.push(path(star(o.w / 2, o.h - m - 5, 3.4), { fill: o.ink, w: 0 }));
                return out;
            }
        }
    };

    function rect(x, y, w, h) { return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]]; }
    function corners(o, m, fn) {
        fn(m, m); fn(o.w - m, m); fn(o.w - m, o.h - m); fn(m, o.h - m);
    }

    var ORDER = ['classic', 'guilloche', 'laurel', 'deco', 'meander', 'scroll', 'braid', 'wave', 'corners', 'kids', 'none'];

    function list() { return ORDER.slice(); }

    /**
     * build(id, {w, h, ink, accent}) → { prims, inset }
     * w/h in millimetres; ink/accent are '#rrggbb'.
     */
    function build(id, opts) {
        var f = FRAMES[id] || FRAMES.classic;
        var o = {
            w: opts.w, h: opts.h,
            ink: opts.ink || '#1d2b45',
            accent: opts.accent || '#b08b3f'
        };
        // The ornament is drawn for the SHORTER dimension's sense of scale, so
        // an A5 card gets a proportionally lighter frame instead of the A4 one
        // shrunk (which reads as clip-art) or kept (which swallows the card).
        var prims = f.build(o) || [];
        var k = Math.min(1.25, Math.max(0.72, Math.min(o.w, o.h) / 210));
        if (k !== 1) prims.forEach(function (p) { if (p.w) p.w = Math.round(p.w * k * 1000) / 1000; });
        return { prims: prims, inset: Math.round(f.inset * k * 10) / 10 };
    }

    /** Optional background guilloché medallion, drawn very faintly.
     *  Three nested hypotrochoids with different tooth counts: their crossings
     *  are the whole effect, so the ratios are deliberately not tidy. */
    function watermark(opts) {
        var cx = opts.cx == null ? opts.w / 2 : opts.cx;
        var cy = opts.cy == null ? opts.h / 2 : opts.cy;
        var R = Math.min(opts.w, opts.h) * (opts.size || 0.30);
        var op = opts.opacity == null ? 0.16 : opts.opacity;
        var c = opts.color;
        return [
            path(hypotrochoid(cx, cy, R, 17, 6, 9, 0), { color: c, w: 0.16, opacity: op }),
            path(hypotrochoid(cx, cy, R * 0.80, 13, 5, 8, 0.31), { color: c, w: 0.15, opacity: op * 0.95 }),
            path(hypotrochoid(cx, cy, R * 0.52, 11, 4, 7, 0.14), { color: c, w: 0.14, opacity: op * 0.9 }),
            { type: 'circle', cx: cx, cy: cy, r: R * 1.10, color: c, w: 0.18, fill: null, opacity: op * 0.45 }
        ];
    }

    /** A rosette medallion used as a printed "seal" placeholder / award badge. */
    function medallion(cx, cy, r, color, accent) {
        var out = [];
        out.push({ type: 'circle', cx: cx, cy: cy, r: r, color: accent, w: 0.7, fill: null });
        out.push({ type: 'circle', cx: cx, cy: cy, r: r * 0.82, color: accent, w: 0.3, fill: null });
        out.push(path(rosette(cx, cy, r * 0.72, 14, 0.3, 1), { color: accent, w: 0.2, opacity: 0.8 }));
        out.push(path(star(cx, cy, r * 0.42), { fill: color, w: 0 }));
        // ribbon tails
        var p = new P();
        p.m(cx - r * 0.5, cy + r * 0.75).l(cx - r * 0.78, cy + r * 2.15).l(cx - r * 0.24, cy + r * 1.78).l(cx + r * 0.02, cy + r * 2.35).l(cx + r * 0.16, cy + r * 0.9).z();
        out.push(path(p.d, { fill: accent, color: accent, w: 0.2, opacity: 0.9 }));
        return out;
    }

    /* Shared with backgrounds.js, which is the same idea one layer down: art
       built from the page instead of stretched onto it. Exported rather than
       copied — a second path builder would drift from this one, and the first
       symptom would be a preview that no longer matches the paper. */
    return {
        list: list, build: build, watermark: watermark, medallion: medallion,
        _perimeter: perimeter, _rosette: rosette, _hypotrochoid: hypotrochoid,
        _P: P, _path: path, _star: star, _leaf: leaf, _harmonic: harmonic
    };
});
